/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/lib/useAsync';

type EntityRef = {
  kind: string;
  name: string;
  title?: string;
  namespace?: string;
};
export type ParentEntityRef = EntityRef & { children?: ParentEntityRef[] };

const getSeviceCollectionHierarchy = (
  serviceCollectionEntities: Entity[],
): ParentEntityRef[] => {
  const entitiesById: { [key in string]: ParentEntityRef } = {};
  const parent: { [key in string]?: string } = {};

  serviceCollectionEntities.forEach(entity => {
    const entityRef: ParentEntityRef = {
      kind: entity.kind,
      name: entity.metadata.name,
      title: entity.metadata.title,
      namespace: entity.metadata.namespace,
    };
    entitiesById[entity.metadata.name] = entityRef;
    const children = [entity.spec?.children].flat() as string[];
    if (entity.spec && children) {
      children.forEach(child => {
        if (child.startsWith('system:')) {
          parent[child] = entity.metadata.name;
        }
      });
      entityRef.children = [];
    }
  });

  serviceCollectionEntities.forEach(entity => {
    const parentId = parent[stringifyEntityRef(entity)];
    // ? If current node is the child of some other node
    if (parentId && entitiesById[parentId]) {
      entitiesById[parentId]?.children?.push({
        kind: entity.kind,
        name: entity.metadata.name,
        title: entity.metadata.title,
        namespace: entity.metadata.namespace,
      });
    }
  });

  return Object.values(entitiesById).filter(
    node => parent[node.name] === undefined,
  );
};

export const useCatalogHierarchy = () => {
  const catalogApi = useApi(catalogApiRef);

  const { value: domains = [] } = useAsync(async () => {
    const filter = {
      kind: 'Domain',
      'spec.type': 'system-collection',
    };
    const resp = await catalogApi.getEntities({
      filter,
      fields: [
        'kind',
        'metadata.namespace',
        'metadata.name',
        'metadata.title',
        'spec.type',
        'spec.children',
        'spec.displayOrder',
      ],
    });
    if (resp.items) {
      return resp.items.sort((item1, item2) => {
        return (
          Number(item1.spec?.displayOrder) - Number(item2.spec?.displayOrder)
        );
      });
    }
    return [];
  }, [catalogApi]);

  const { value: systems = [] } = useAsync(async () => {
    const filter = {
      kind: 'System',
      'spec.type': 'service-collection',
    };
    const resp = await catalogApi.getEntities({
      filter,
      fields: [
        'kind',
        'metadata.namespace',
        'metadata.name',
        'metadata.title',
        'spec.type',
        'spec.children',
        'relations',
      ],
    });
    return getSeviceCollectionHierarchy(resp.items);
  }, [catalogApi]);

  const catalogHierarchy: ParentEntityRef[] = [];
  if (domains.length && systems.length) {
    domains.forEach(domain => {
      const domainChildren: ParentEntityRef[] = [];

      systems.forEach(system => {
        const childIndex = (domain.spec?.children as string[]).indexOf(
          system.name,
        );
        if (childIndex >= 0) {
          domainChildren.splice(childIndex, 0, system);
        }
      });
      catalogHierarchy.push({
        kind: domain.kind,
        name: domain.metadata.name,
        namespace: domain.metadata.namespace,
        title: domain.metadata.title,
        children: domainChildren,
      });
    });
  }
  return { catalogHierarchy };
};
